function machine(def) {
    return def;
}
/* ------------------------------------------------------------------ */
/* Concours                                                            */
/* ------------------------------------------------------------------ */
export const COMPETITION_STATES = [
    'draft',
    'published',
    'open',
    'suspended',
    'closed',
    'processing',
    'provisional_results',
    'final_results',
    'archived',
];
export const competitionMachine = machine({
    states: COMPETITION_STATES,
    initial: 'draft',
    transitions: [
        { from: 'draft', to: 'published', permission: 'concours.publish', reasonRequired: false },
        { from: 'published', to: 'draft', permission: 'concours.publish', reasonRequired: true },
        { from: 'published', to: 'open', permission: 'concours.open', reasonRequired: false },
        { from: 'published', to: 'open', permission: null, reasonRequired: false, actor: 'system' },
        { from: 'open', to: 'suspended', permission: 'concours.suspend', reasonRequired: true },
        { from: 'suspended', to: 'open', permission: 'concours.suspend', reasonRequired: true },
        { from: 'open', to: 'closed', permission: 'concours.close', reasonRequired: false },
        { from: 'open', to: 'closed', permission: null, reasonRequired: false, actor: 'system' },
        { from: 'closed', to: 'open', permission: 'concours.extend', reasonRequired: true },
        { from: 'closed', to: 'processing', permission: 'concours.update', reasonRequired: false },
        {
            from: 'processing',
            to: 'provisional_results',
            permission: 'results.publish',
            reasonRequired: false,
        },
        {
            from: 'processing',
            to: 'final_results',
            permission: 'results.publish',
            reasonRequired: false,
        },
        {
            from: 'provisional_results',
            to: 'final_results',
            permission: 'results.publish',
            reasonRequired: false,
        },
        {
            from: 'final_results',
            to: 'archived',
            permission: 'concours.archive',
            reasonRequired: false,
        },
    ],
});
/** États dans lesquels un candidat peut créer / modifier / soumettre un dossier. */
export const COMPETITION_ACCEPTS_APPLICATIONS = ['open'];
/** États visibles sur le portail public. */
export const COMPETITION_PUBLIC_STATES = [
    'published',
    'open',
    'suspended',
    'closed',
    'processing',
    'provisional_results',
    'final_results',
];
/* ------------------------------------------------------------------ */
/* Dossier de candidature                                              */
/* ------------------------------------------------------------------ */
export const APPLICATION_STATES = [
    'draft',
    'ready',
    'submitted',
    'under_review',
    'to_complete',
    'admissible',
    'not_admissible',
    'withdrawn',
    'cancelled',
];
export const applicationMachine = machine({
    states: APPLICATION_STATES,
    initial: 'draft',
    transitions: [
        { from: 'draft', to: 'ready', permission: null, reasonRequired: false, actor: 'system' },
        { from: 'ready', to: 'draft', permission: null, reasonRequired: false, actor: 'system' },
        { from: 'ready', to: 'submitted', permission: null, reasonRequired: false, actor: 'candidate' },
        {
            from: 'submitted',
            to: 'under_review',
            permission: 'candidature.review',
            reasonRequired: false,
        },
        {
            from: 'under_review',
            to: 'to_complete',
            permission: 'candidature.request_complement',
            reasonRequired: true,
        },
        {
            from: 'to_complete',
            to: 'under_review',
            permission: null,
            reasonRequired: false,
            actor: 'candidate',
        },
        // Délai de complément dépassé : retour en instruction (tâche planifiée).
        {
            from: 'to_complete',
            to: 'under_review',
            permission: null,
            reasonRequired: true,
            actor: 'system',
        },
        /*
         * Recevabilité (phase 12, ADR-029) : un agent PROPOSE (`candidature.decide`),
         * une autre personne VALIDE (`candidature.validate`) ; c'est la validation qui
         * fait changer l'état. Un impayé à l'échéance (`after_submit`) est proposé par
         * le système, quel que soit l'état d'instruction du dossier.
         */
        {
            from: 'under_review',
            to: 'admissible',
            permission: 'candidature.validate',
            reasonRequired: false,
        },
        {
            from: 'under_review',
            to: 'not_admissible',
            permission: 'candidature.validate',
            reasonRequired: true,
        },
        {
            from: 'submitted',
            to: 'not_admissible',
            permission: 'candidature.validate',
            reasonRequired: true,
        },
        {
            from: 'to_complete',
            to: 'not_admissible',
            permission: 'candidature.validate',
            reasonRequired: true,
        },
        { from: 'draft', to: 'withdrawn', permission: null, reasonRequired: false, actor: 'candidate' },
        { from: 'ready', to: 'withdrawn', permission: null, reasonRequired: false, actor: 'candidate' },
        {
            from: 'submitted',
            to: 'withdrawn',
            permission: null,
            reasonRequired: true,
            actor: 'candidate',
        },
        { from: 'submitted', to: 'cancelled', permission: 'candidature.cancel', reasonRequired: true },
        {
            from: 'under_review',
            to: 'cancelled',
            permission: 'candidature.cancel',
            reasonRequired: true,
        },
    ],
});
/* ------------------------------------------------------------------ */
/* Paiement                                                            */
/* ------------------------------------------------------------------ */
export const PAYMENT_STATES = [
    'not_required',
    'pending',
    'processing',
    'confirmed',
    'failed',
    'cancelled',
    'refunded',
];
export const paymentMachine = machine({
    states: PAYMENT_STATES,
    initial: 'pending',
    transitions: [
        {
            from: 'pending',
            to: 'processing',
            permission: null,
            reasonRequired: false,
            actor: 'candidate',
        },
        {
            from: 'processing',
            to: 'confirmed',
            permission: null,
            reasonRequired: false,
            actor: 'system',
        },
        { from: 'processing', to: 'failed', permission: null, reasonRequired: false, actor: 'system' },
        {
            from: 'processing',
            to: 'cancelled',
            permission: null,
            reasonRequired: false,
            actor: 'system',
        },
        { from: 'failed', to: 'pending', permission: null, reasonRequired: false, actor: 'candidate' },
        {
            from: 'cancelled',
            to: 'pending',
            permission: null,
            reasonRequired: false,
            actor: 'candidate',
        },
        { from: 'pending', to: 'confirmed', permission: 'payment.manual_entry', reasonRequired: true },
        // Validation manuelle d'un paiement en ligne, après vérification chez le prestataire.
        {
            from: 'processing',
            to: 'confirmed',
            permission: 'payment.manual_entry',
            reasonRequired: true,
        },
        { from: 'failed', to: 'confirmed', permission: 'payment.manual_entry', reasonRequired: true },
        {
            from: 'cancelled',
            to: 'confirmed',
            permission: 'payment.manual_entry',
            reasonRequired: true,
        },
        { from: 'confirmed', to: 'refunded', permission: 'payment.refund', reasonRequired: true },
    ],
});
/** Mode de paiement par concours (ADR-001). */
export const PAYMENT_TIMINGS = [
    'before_submit',
    'after_submit',
    'after_admissibility',
    'not_required',
];
/* ------------------------------------------------------------------ */
/* Notes                                                               */
/* ------------------------------------------------------------------ */
export const GRADE_STATES = ['draft', 'submitted', 'validated', 'unlocked'];
export const gradeMachine = machine({
    states: GRADE_STATES,
    initial: 'draft',
    transitions: [
        { from: 'draft', to: 'submitted', permission: 'notes.submit', reasonRequired: false },
        { from: 'submitted', to: 'draft', permission: 'notes.correct', reasonRequired: true },
        { from: 'submitted', to: 'validated', permission: 'notes.validate', reasonRequired: false },
        { from: 'validated', to: 'unlocked', permission: 'notes.unlock', reasonRequired: true },
        { from: 'unlocked', to: 'submitted', permission: 'notes.submit', reasonRequired: true },
    ],
});
/* ------------------------------------------------------------------ */
/* Classement                                                          */
/* ------------------------------------------------------------------ */
export const RANKING_STATES = ['draft', 'under_control', 'approved', 'published'];
export const rankingMachine = machine({
    states: RANKING_STATES,
    initial: 'draft',
    transitions: [
        { from: 'draft', to: 'under_control', permission: 'results.calculate', reasonRequired: false },
        { from: 'under_control', to: 'draft', permission: 'results.calculate', reasonRequired: true },
        { from: 'under_control', to: 'approved', permission: 'results.approve', reasonRequired: false },
        { from: 'approved', to: 'published', permission: 'results.publish', reasonRequired: false },
    ],
});
/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
export function findTransitions(sm, from, to) {
    return sm.transitions.filter((t) => t.from === from && t.to === to);
}
export function canTransition(sm, from, to) {
    return findTransitions(sm, from, to).length > 0;
}
export function nextStates(sm, from) {
    return [...new Set(sm.transitions.filter((t) => t.from === from).map((t) => t.to))];
}
//# sourceMappingURL=states.js.map